import { Loader2, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { JobTrackerState } from '@nucleus-commerce/admin-kit/jobs';
import type { JobStatus } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { JobStatusBadge } from './job-status-badge';

export interface TrackedJobStatusProps<TJob extends { status: JobStatus }> {
  tracker: JobTrackerState<TJob>;
  title: string;
  /** Progress line, result actions; rendered under the status. */
  children?: ReactNode;
}

/**
 * Live status of the job this screen just started. Polling stops at a terminal status; after the
 * tracker's timeout it hands over to a manual check with a cooldown.
 */
export function TrackedJobStatus<TJob extends { status: JobStatus; error?: string | null }>({
  tracker,
  title,
  children,
}: TrackedJobStatusProps<TJob>) {
  const { t } = useTranslation('importsExports');
  const job = tracker.job;
  if (!job) return null;

  return (
    <section
      aria-label={title}
      aria-live="polite"
      className="bg-muted/40 flex flex-col gap-2 rounded-lg border p-3 text-sm"
      data-testid="tracked-job"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{title}</span>
        <JobStatusBadge status={job.status} error={job.error ?? null} />
      </div>
      {tracker.status === 'polling' ? (
        <p className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
          <Loader2 className="size-3 animate-spin" aria-hidden />
          {t('tracker.polling')}
        </p>
      ) : null}
      {tracker.status === 'stalled' ? (
        <div className="flex items-center gap-2">
          <p className="text-muted-foreground text-xs">{t('tracker.stalled')}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!tracker.canCheck}
            onClick={tracker.checkNow}
          >
            <RefreshCw aria-hidden />
            {t('tracker.check')}
          </Button>
        </div>
      ) : null}
      {children}
    </section>
  );
}
